import React from 'react';
import { useIntl } from 'react-intl';
import { COVER_EXPORT_PX } from 'fym-shared/src/music24/coverDoc';
import { canUseCoverAi } from 'fym-shared/src/music24/coverEditorPlans';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { useCoverEditor } from '../CoverEditorContext';
import { coverEditorMessages } from '../coverEditorMessages';
import type { CoverSaveState } from '../doc/useCoverAutosave';
import { SaveState } from './SaveState';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

export const COVER_ZOOM_STEPS = [0.5, 0.75, 1, 1.5, 2] as const;

interface EditorTopBarProps {
  saveState: CoverSaveState;
  version: number;
  zoom: number;
  onZoom: (zoom: number) => void;
  onBack: () => void;
  onDownload: () => void;
  isDownloading: boolean;
  onUseAsCover: () => void;
  isApplying: boolean;
}

/**
 * The top bar of the editor (design cover-editor b1): back, the song, Undo and Redo, the zoom, the
 * save state, the plan, Download and "Use as cover".
 */
const EditorTopBarComponent: React.FC<EditorTopBarProps> = (props) => {
  const { saveState, version, zoom, onZoom, onBack, onDownload, isDownloading, onUseAsCover, isApplying } = props;
  const intl = useIntl();
  const { data, dispatch, canUndo, canRedo, readOnly } = useCoverEditor();
  const { song } = data;
  const zoomIndex = COVER_ZOOM_STEPS.findIndex((step) => step === zoom);
  const zoomOut = () => onZoom(COVER_ZOOM_STEPS[Math.max(0, zoomIndex - 1)] ?? 1);
  const zoomIn = () => onZoom(COVER_ZOOM_STEPS[Math.min(COVER_ZOOM_STEPS.length - 1, zoomIndex + 1)] ?? 1);

  return (
    <header className={styles.top}>
      <button type="button" className={styles.back} onClick={onBack}>
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M15 5l-7 7 7 7" />
        </svg>
        <span className={styles.backText}>{intl.formatMessage(coverEditorMessages.back)}</span>
      </button>
      <div className={styles.song}>
        {song.artworkUrl !== null && <img src={song.artworkUrl} alt="" />}
        <div className={styles.songText}>
          <b>{song.title}</b>
          <small>
            {intl.formatMessage(coverEditorMessages.songLine, { artist: song.artistName, size: COVER_EXPORT_PX })}
          </small>
        </div>
      </div>
      <span className={styles.topSeparator} />
      <button
        type="button"
        className={styles.topButton}
        aria-label={intl.formatMessage(coverEditorMessages.undo)}
        title={intl.formatMessage(coverEditorMessages.undo)}
        disabled={!canUndo || readOnly}
        onClick={() => dispatch({ type: 'undo' })}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M9 14L4 9l5-5" />
          <path d="M4 9h10a6 6 0 0 1 0 12h-3" />
        </svg>
      </button>
      <button
        type="button"
        className={styles.topButton}
        aria-label={intl.formatMessage(coverEditorMessages.redo)}
        title={intl.formatMessage(coverEditorMessages.redo)}
        disabled={!canRedo || readOnly}
        onClick={() => dispatch({ type: 'redo' })}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M15 14l5-5-5-5" />
          <path d="M20 9H10a6 6 0 0 0 0 12h3" />
        </svg>
      </button>
      <span className={styles.zoom}>
        <button
          type="button"
          aria-label={intl.formatMessage(coverEditorMessages.zoomOut)}
          disabled={zoomIndex <= 0}
          onClick={zoomOut}
        >
          −
        </button>
        <span>
          {zoom === 1
            ? intl.formatMessage(coverEditorMessages.zoomFit)
            : intl.formatMessage(coverEditorMessages.zoomValue, { percent: Math.round(zoom * 100) })}
        </span>
        <button
          type="button"
          aria-label={intl.formatMessage(coverEditorMessages.zoomIn)}
          disabled={zoomIndex >= COVER_ZOOM_STEPS.length - 1}
          onClick={zoomIn}
        >
          +
        </button>
      </span>
      <SaveState state={saveState} version={version} readOnly={readOnly} />
      <span className={styles.grow} />
      <span className={styles.planChip}>
        {intl.formatMessage(canUseCoverAi(data.plan) ? coverEditorMessages.planAi : coverEditorMessages.planBank)}
      </span>
      <SMLButton size="sm" outline omitCap onClick={onDownload} isLoading={isDownloading} disabled={isDownloading}>
        {intl.formatMessage(coverEditorMessages.download)}
      </SMLButton>
      <SMLButton size="sm" omitCap onClick={onUseAsCover} isLoading={isApplying} disabled={isApplying || readOnly}>
        {intl.formatMessage(coverEditorMessages.useAsCover)}
      </SMLButton>
    </header>
  );
};

export const EditorTopBar = TypedMemo(EditorTopBarComponent);
